import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import React, { RefObject, useEffect, useRef, useState } from 'react';

import { ReactComponent as KnowEachOtherArtBottomSvg } from '../../../../assets/know_each_other/art_bottom.svg';
import { ReactComponent as KnowEachOtherArtTopSvg } from '../../../../assets/know_each_other/art_top.svg';
import { ReactComponent as CarouselBaseSvg } from '../../../../assets/know_each_other/carousel_base.svg';
import { ReactComponent as CarouselFeedSvg } from '../../../../assets/know_each_other/carousel_feed.svg';
import { ReactComponent as CarouselNavbarSvg } from '../../../../assets/know_each_other/carousel_navbar.svg';
import { ReactComponent as CarouselTimelineSvg } from '../../../../assets/know_each_other/carousel_timeline.svg';
import { OnboardingStep, useUpdateUserSettingsMutation } from '../../../../graphql/gen/types';
import { spacings, TextSizesEnum } from '../../../../theme';
import { ButtonColor, PlexButton } from '../../../shared/primitives/PlexButton';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { OnboardingStepSelector } from '../OnboardingStepSelector';
import { KnowEachOtherCarouselPager } from './KnowEachOtherCarouselPager';
import { KnowEachOtherCarouselSwitch } from './KnowEachOtherCarouselSwitch';

interface Props {
  changeOnboardingStep: (step: OnboardingStep) => void;
  completedOnboardingSteps: OnboardingStep[];
  currentStep: OnboardingStep;
  onNext: () => void;
}

export const KnowEachOther = ({
  changeOnboardingStep,
  completedOnboardingSteps,
  currentStep,
  onNext,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();
  const carouselRef = useRef<SVGSVGElement>(null);
  const [, setLoadedRef] = useState(false);
  const [carouselPage, changeCarouselPage] = useState(0);
  const carouselItems = [
    {
      title: 'Your Plex Feed',
      message:
        'The Plex Feed displays important updates from your Plex teams or any Plex\nyou’ve subscribed to. Get more info, share or comment on any update or\npost your own to inform your key stakeholders & team!',
      svg: CarouselFeedSvg,
    },
    {
      title: 'Your Plex Base',
      message:
        'This is Plex Base - a searchable, FAQ-style knowledge base where you can\nask questions and get answers to anything related to your company’s\nproducts. If you’re the product expert, create FAQs to share knowledge!',
      svg: CarouselBaseSvg,
    },
    {
      title: 'Your Plex Timeline',
      message:
        'The Plex Timeline let’s you see the bigger picture - including important\nupdates, events & milestones for any or all products. Track product launch\ndates, blockers, dependencies or just find out what’s coming next.',
      svg: CarouselTimelineSvg,
    },
    {
      title: 'Navigating Plex',
      message:
        'With Plex, every key update, event or piece of knowledge is searchable\nfrom one place. Use the left navigation to view just the Plexes you’re\ninterested in or apply labels to find specific types of updates and FAQs.',
      svg: CarouselNavbarSvg,
    },
  ];
  const CurrentCarouselSVG = carouselItems[carouselPage].svg;
  useEffect(() => {
    setLoadedRef(true);
  }, [setLoadedRef]);

  const { computedWidth, computedExtraMargin } = getMarginsCarousel(carouselRef);

  return (
    <div className={classes.root}>
      <div className={classes.topContainer}>
        <div className={classes.topContainerWrapper}>
          <div className={classes.leftContainer}>
            <div>
              <KnowEachOtherArtTopSvg className={classes.artTop} />
              <OnboardingStepSelector
                changeOnboardingStep={changeOnboardingStep}
                completedOnboardingSteps={completedOnboardingSteps}
                currentStep={currentStep}
              />
            </div>
            <KnowEachOtherArtBottomSvg className={classes.artBottom} />
          </div>
          <div className={classes.carousel}>
            <div
              className={classes.titleContainer}
              style={{ marginLeft: (12 / 100) * computedWidth + computedExtraMargin }}
            >
              <div className={classes.titleTextWrapper}>
                <PlexTypography
                  className={classes.title}
                  size={TextSizesEnum.XXXLarge}
                  text={carouselItems[carouselPage].title}
                />
                <PlexTypography
                  className={classes.message}
                  size={TextSizesEnum.Normal}
                  text={carouselItems[carouselPage].message}
                />
              </div>
              <KnowEachOtherCarouselSwitch
                pageIndex={carouselPage}
                pagesCount={carouselItems.length}
                onChangePage={(newPageIndex): void => {
                  changeCarouselPage(newPageIndex);
                }}
              />
            </div>
            <CurrentCarouselSVG ref={carouselRef} className={classes.artCarousel} />
            <KnowEachOtherCarouselPager pageIndex={carouselPage} pagesCount={carouselItems.length} />
          </div>
        </div>
      </div>
      <div className={classes.bottomContainer}>
        {loading && <CircularProgress size={24} />}
        <PlexButton
          className={classes.skipButton}
          label={'Save & Finish Later'}
          onClick={onNext}
          color={ButtonColor.TransparentWhite}
        />
        <PlexButton
          className={classes.nextButton}
          label={'Next'}
          onClick={(): void => {
            updateUserSettings({
              variables: {
                userSettingsInput: {
                  completedOnboardingStep: OnboardingStep.KnowEachOther,
                },
              },
              update: (_, { errors }) => {
                if (!errors?.length) {
                  onNext();
                } else {
                  console.error('got err when next on get started:', errors);
                }
              },
            });
          }}
          color={ButtonColor.Primary}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'auto',
    height: '100vh',
    width: '100%',
    paddingTop: 80,
  },
  topContainer: {
    display: 'flex',
    overflowY: 'hidden',
    height: '100%',
  },
  topContainerWrapper: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  leftContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 348,
    minWidth: 348,
    justifyContent: 'space-between',
  },
  leftTopContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
  },
  artTop: {
    marginLeft: -24,
    marginTop: -80,
    height: 260,
    willChange: 'transform', // smoother scroll (GPU driven)
  },
  artBottom: {
    marginTop: spacings.small,
    marginLeft: 48,
    willChange: 'transform', // smoother scroll (GPU driven)
  },
  carousel: {
    display: 'flex',
    flex: 1,
    height: '100%',
    flexDirection: 'column',
    alignSelf: 'flex-start',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '12%', // based on the space given in svg
    marginRight: '6%', // based on the space given in svg
    justifyContent: 'space-between',
    marginBottom: spacings.xlarge,
  },
  titleTextWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    color: '#464663',
    fontWeight: 'bold',
    letterSpacing: '0.25px',
    marginTop: 50,
  },
  message: {
    color: '#464663',
    marginTop: spacings.xlarge,
    opacity: 0.8,
    height: 60,
  },
  artCarousel: {
    width: '100%',
    height: '100%',
    display: 'flex',
    minWidth: 800,
    willChange: 'transform', // smoother scroll (GPU driven)
    marginBottom: -spacings.xxlarge, // in order to overlap the page circles
  },
  bottomContainer: {
    display: 'flex',
    width: '100%',
    height: 100,
    flexDirection: 'row',
    backgroundColor: '#6588FD',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: spacings.xlarge,
    paddingBottom: spacings.xlarge,
    paddingRight: spacings.xlarge * 2,
  },
  skipButton: {
    height: 52,
    borderRadius: 36,
  },
  nextButton: {
    height: 52,
    width: 112,
    borderRadius: 36,
  },
}));

const getMarginsCarousel = (
  carouselRef?: RefObject<SVGSVGElement>
): { computedWidth: number; computedExtraMargin: number } => {
  // TODO this will not work when window is resized

  const originalWidth = 1068;
  const originalHeight = 666;
  let computedWidth = 0;
  let computedExtraMargin = 0;

  if (!!carouselRef?.current?.clientWidth) {
    const displayedSvgAspectRatio = carouselRef.current.clientWidth / carouselRef.current.clientHeight;
    const originalSvgAspectRatio = originalWidth / originalHeight;
    if (displayedSvgAspectRatio > originalSvgAspectRatio) {
      // if the displayed svg has a wider aspect ratio, then calculate the width based on the original width and the
      // difference between the heights
      computedWidth = originalWidth * (carouselRef.current.clientHeight / originalHeight);
    } else {
      // if the displayed svg has a taller aspect ratio, then use the displayed svg's width
      computedWidth = carouselRef.current.clientWidth;
    }

    // svg is aligned in center, so we also need to find the size of the empty space around the svg
    computedExtraMargin = (carouselRef.current.clientWidth - computedWidth) / 2;
  }
  return { computedWidth, computedExtraMargin };
};
